import { UserMeshMinimal, UserMeshBasic, MeshesForUser, UserMeshType } from "./UserMesh";
import { UserSound } from "./UserSound";
import { Scene, Nullable, StandardMaterial, AbstractMesh, VideoTexture, Color3, Mesh, Material, TransformNode, Vector2, Vector3, DynamicTexture, MeshBuilder } from "@babylonjs/core";
import TestVideo from '../../../assets/test.mp4';
import { getDistance, calculateAngleInfoFromSourceToTarget } from "./mathUtils";
import { User } from "./User";
import { createMaterialForUserMesh } from "./userMeshUtils";
import { getIsSlowSettingEnabled } from "../../../services/LocalStorage";

export class UserMeshManager {
  private scene: Scene;
  private _userMeshType: UserMeshType = 'UserMeshMinimal';

  private userMeshes: { [userId: string]: UserMeshBasic | UserMeshMinimal };

  public localUserId: string = '';
  private localUserMesh: TransformNode;
 
  private _headMesh: Nullable<Mesh> = null;
  private _torsoMesh: Nullable<Mesh> = null;
  private _tvMesh: Nullable<Mesh> = null;
  private _nameMesh: Nullable<Mesh> = null;
  private _nameMaterial: Nullable<StandardMaterial> = null;
  private _tvMaterial: Nullable<StandardMaterial> = null;
  private _userMaterial: Nullable<StandardMaterial> = null;

  private nonInstancedUserId: string = '';
  private refsHeadMesh = 0;
  private refsTorsoMesh = 0;
  private refsTvMesh = 0;
  private refsNameMesh = 0;
  private refsTvMaterial = 0;
  private refsNameMaterial = 0;
  constructor(scene: Scene) {
    this.scene = scene;
    this.userMeshes = {};
    this.localUserMesh = new TransformNode('localUserMesh', this.scene);
  }

  public attachAudioStreamToMesh = (userId: string, stream: MediaStream) => {
    const userMesh: UserMeshMinimal | UserMeshBasic = this.userMeshes[userId];
    const existingUserSound = userMesh.micSound;
    if (existingUserSound) existingUserSound.disconnect();
    const userSound = new UserSound(userMesh.user.userName, stream, this.scene);
    userMesh.micSound = userSound;
    userMesh.micSound.applyAudioFilters();
  }
  
  public attachVideoStreamToMesh = async (userId: string, stream: MediaStream) => {
    const userMesh = this.userMeshes[userId];
    this.cleanUpTvMaterial(userMesh.tv.material);
    const material = userMesh.tv.material = new StandardMaterial('videoMaterial', this.scene);
    const track = stream.getVideoTracks()[0];
    material.diffuseTexture = await VideoTexture.CreateFromStreamAsync(this.scene, stream, undefined);
    track.onmute = () => {
      material.getActiveTextures().forEach(texture => texture.dispose());
      material.diffuseTexture = null;
      material.diffuseColor = new Color3(0,0,0);
    }
    track.onunmute = async () => {
      material.diffuseColor = new Color3(1,1,1);
      material.diffuseTexture = await VideoTexture.CreateFromStreamAsync(this.scene, stream, undefined);
    }
  }
  
  public attachTestAudioStreamToMesh = (userId: string, src: string) => {
    const userMesh: UserMeshMinimal | UserMeshBasic = this.userMeshes[userId];
    const userSound: UserSound = new UserSound(userId, src, this.scene);
    userMesh.micSound = userSound;
    userMesh.micSound.applyAudioFilters();
  }
  
  public attachTestVideoStreamToMesh = async (userId: string) => {
    const src = TestVideo;
    const material = this.userMeshes[userId].tv.material = new StandardMaterial('videoMaterial', this.scene);
    const videoTexture = new VideoTexture('test-video-texture', src, this.scene);
    videoTexture.video.muted = true;
    material.diffuseTexture = videoTexture;
  }
  
  public dispose = () => {
    const nonInstancedUserIds = this.getUserIds().filter(id => id !== this.nonInstancedUserId);
    for (const userId of nonInstancedUserIds) {
      this.removeAllUserMeshFromScene(userId);
    }
    this.removeAllUserMeshFromScene(this.nonInstancedUserId);
    this._tvMaterial?.dispose();
    this._userMaterial?.dispose();
    this.scene.removeTransformNode(this.localUserMesh);
  }

  private cleanUpTvMaterial = (material: Nullable<Material>) => {
    if (!material) return;
    const hasTexture = material.getActiveTextures().length > 0;
    if (hasTexture) {
      material.getActiveTextures().forEach(t => t.dispose());
      material.dispose();
    } else {
      this.refsTvMaterial--;
      if (this.refsTvMaterial === 0 && this._tvMaterial) {
        this._tvMaterial.dispose();
        this._tvMaterial = null;
      }
    }
  }

  public removeAllUserMeshFromScene = (userId: string) => {
    const usermesh: MeshesForUser = this.userMeshes[userId];
    if (!usermesh) return;
    usermesh.micSound?.sound.stop();
    usermesh.micSound?.sound.detachFromMesh();
    usermesh.micSound?.sound.dispose();
    this.scene.removeMesh(usermesh.tv);
    this.scene.removeMesh(usermesh.torso);
    this.scene.removeMesh(usermesh.head);
    this.scene.removeTransformNode(usermesh.torsoTransformNode);
    this.scene.removeTransformNode(usermesh.headTransformNode);
    this.scene.removeMesh(usermesh.nameTag);
    this.scene.removeTransformNode(usermesh.root);

    this.cleanUpTvMaterial(usermesh.tv.material);
    const numUserMeshes =  Object.keys(this.userMeshes).length;
    if (userId !== this.nonInstancedUserId || numUserMeshes === 1) {
      usermesh.tv.dispose()
      usermesh.torso.dispose();
      usermesh.head.dispose();
      usermesh.torsoTransformNode.dispose();
      usermesh.headTransformNode.dispose();
      usermesh.nameTag.material?.getActiveTextures().forEach(texture => texture.dispose());
      usermesh.nameTag.material?.dispose();
      usermesh.nameTag.dispose();
      usermesh.root.dispose();
    }
  }

  public set userMeshType(type: UserMeshType) {
    this._userMeshType = type;
  }

  private _heightForHead = 0.9;
  private _heightForNameTag = 1.6; // heightForHead + 0.7
  private _heightForTorso = 0.1;
  public get heightForHead() {
    return this._heightForHead;
  } 
  public get heightForNameTag() {
    return this._heightForNameTag;
  } 
  public get heightForTorso() {
    return this._heightForTorso;
  } 

  public get userMaterial() {
    if (!this._userMaterial) {
      const material = new StandardMaterial('userMaterial', this.scene);
      material.diffuseColor = new Color3(238/255, 238/255, 238/255);
      this._userMaterial = material;
    }
    return this._userMaterial;
  }

  public set userMaterial(material: Nullable<StandardMaterial>) {
    this._userMaterial = material;
  }

  private get tvMaterial() {
    if (!this._tvMaterial) {
      const material = new StandardMaterial('tvMaterial', this.scene);
      material.diffuseColor = new Color3(0, 0, 0);
      this._tvMaterial = material;
    }
    this.refsTvMaterial++;
    return this._tvMaterial;
  }

  private get tvMesh() {
    if (!this._tvMesh) {
      const tv = MeshBuilder.CreatePlane('userMesh.tv', {
        width: 0.9,
        height: 0.9,
        sideOrientation: Mesh.FRONTSIDE,
      }, this.scene);
      tv.material = this.tvMaterial;
      this._tvMesh = tv;
      return this._tvMesh;
    } else {
      this.refsTvMaterial++;
      const clonedTvMesh = this._tvMesh.clone(`userMesh.tv.${++this.refsTvMesh}`);
      clonedTvMesh.material = this.tvMaterial;
      return clonedTvMesh;
    }
  }

  private get nameMaterial() {
    if (!this._nameMaterial) {
      const material = new StandardMaterial('nameMaterial', this.scene);
      material.backFaceCulling = false;
      this._nameMaterial = material;
      return this._nameMaterial;
    } else {
      return this._nameMaterial.clone(`name.material.${++this.refsNameMaterial}`);
    }
  }

  private get nameMesh() {
    if (!this._nameMesh) {
      const textureDimensions = this.getTextureDimensions();
    
      const nameTag = MeshBuilder.CreatePlane('userMesh.nameTag', {
        height: this.textMeshOptions.fontSize / this.textMeshOptions.heightDiviser,
        width: (this.textMeshOptions.fontSize / this.textMeshOptions.heightDiviser) / textureDimensions.height * textureDimensions.width,
        sideOrientation: Mesh.DOUBLESIDE,
      }, this.scene);
      nameTag.rotation = new Vector3(0, Math.PI, 0);
      nameTag.material = this.nameMaterial;
      this._nameMesh = nameTag;
      return this._nameMesh;
    } else {
      const clonedNameMesh = this._nameMesh.clone(`userMesh.nameTag.${++this.refsNameMesh}`);
      clonedNameMesh.material = this.nameMaterial;
      return clonedNameMesh;
    }
  }

  private applyTextureToNameTag = (nameTag: AbstractMesh, nameMaterial: StandardMaterial, text: string) => {
    const slicedText = text.slice(0, this.textMeshOptions.maxTextLength);
  
    const textureDimensions = this.getTextureDimensions();
    
    const nameTexture = new DynamicTexture('nameTexture', { width: textureDimensions.width, height: textureDimensions.height }, this.scene, false);
  
    nameMaterial.diffuseTexture = nameTexture;
    nameTag.material = nameMaterial;
  
    nameTexture.hasAlpha = true;
    nameTexture.getAlphaFromRGB = true;
    nameTexture.drawText(slicedText, null, null, `bold ${this.textMeshOptions.fontSize}px ${this.textMeshOptions.font}`, this.textMeshOptions.textColor, `rgba(0, 0, 0, ${this.textMeshOptions.backgroundAlpha})`);
  
    return nameTag; // is the return necessary?
  }

  private getTextureDimensions = () => ({ 
    width: this.textMeshOptions.maxTextLength * this.textMeshOptions.widthMultiplier * 10,
    height: this.textMeshOptions.heightMultiplier * this.textMeshOptions.fontSize 
  });
  
  private textMeshOptions = {
    font: 'Lato',
    maxTextLength: 16,
    widthMultiplier: 6,
    fontSize: 48,
    heightDiviser: 96,
    heightMultiplier: 1.5,
    backgroundAlpha: 0,
    textColor: '#ffffff'
  };

  private get headMesh() {
    if (!this._headMesh) {
      const head = MeshBuilder.CreateBox(`userMesh.head`, {
        width: 1,
        height: 0.05,
        depth: 1,
      }, this.scene);
      head.material = this.userMaterial;
      this._headMesh = head;
      return this._headMesh;
    } else {
      return this._headMesh.createInstance(`userMesh.head.${++this.refsHeadMesh}`);
    }
  }

  private get torsoMesh() {
    if (!this._torsoMesh) {
      const torso = this._userMeshType === 'UserMeshBasic' ? this.getBasicTorsoMesh() : this.getMinimalTorsoMesh();
      torso.material = this._userMaterial;
      this._torsoMesh = torso;
      return this._torsoMesh;
    } else {
      return this._torsoMesh.createInstance(`userMesh.torso.${++this.refsTorsoMesh}`);
    }
  }

  private getBasicTorsoMesh = () => {
    const meshName = 'userMesh.torso';
    const torso = MeshBuilder.CreatePolyhedron(meshName, {
      type: 0,
      sizeX: 0.15,
      sizeY: 0.15,
      sizeZ: 0.25,
    }, this.scene);
    torso.rotation.x = Math.PI/2;
    return torso;
  }
  
  private getMinimalTorsoMesh = () => {
    const meshName = 'userMesh.torso';
    return MeshBuilder.CreateBox(meshName, {
      width: 0.45,
      height: 0.35,
      depth: 0.1,
    }, this.scene);
  }
  
  // public createMeshesForUser = (user: User, head: AbstractMesh, torso: AbstractMesh, tv: AbstractMesh, nameTag: AbstractMesh, material: StandardMaterial) => {
  public createMeshesForUser = (user: User) => {
    const root = new TransformNode(`rootTransformNode${user.id}`, this.scene);

    const head = this.headMesh;
    const torso = this.torsoMesh;
    const tv = this.tvMesh;
    const nameTag = this.nameMesh;
  
    const headTransformNode = new TransformNode(`headTransformNode${user.id}`, this.scene);
    headTransformNode.parent = root;
    head.parent = headTransformNode;
    
    const headBbox = head.getBoundingInfo().boundingBox;
    const scale = 1;
    // const scale = 1 / (headBbox.maximum.x - headBbox.minimum.x);
    head.scalingDeterminant = scale;
    head.position = headBbox.center.scale(-scale);
    head.position.y = 0.8;
    
    tv.parent = headTransformNode;
    tv.rotation = new Vector3(-Math.PI / 2, 0, 0);
    // tv.rotation = new Vector3(-Math.PI, 0, 0);
    tv.position = new Vector3(head.position.x, head.position.y - 0.03, head.position.z);
    // tv.position = new Vector3(0, headBbox.center.scale(scale).y + 1, (headBbox.maximum.z - headBbox.center.z) * scale + 0.001);
    headTransformNode.setPivotPoint(tv.position);
    headTransformNode.rotation.x = -Math.PI/2;
    // headTransformNode.setPivotPoint(tv.position.subtract(new Vector3(0, (headBbox.maximum.y - headBbox.minimum.y) * scale, 0)));
  
    // const nameTag = createNameTagMesh(user, headBbox, root, this.scene);
    const nameMaterial = nameTag.material as StandardMaterial;
    this.applyTextureToNameTag(nameTag, nameMaterial, user.userName);
    nameTag.parent = root;
    nameTag.position = new Vector3(head.position.x, head.position.y + 0.7, head.position.z);
  
    const torsoTransformNode = new TransformNode(`torsoTransformNode${user.id}`, this.scene);
    torsoTransformNode.parent = root;
    torso.parent = torsoTransformNode;
    torso.scalingDeterminant = scale;
    // const torsoBbox = torso.getBoundingInfo().boundingBox;
    // torso.position = torsoBbox.center.scale(-scale);
    torso.position.y = 0;
    torsoTransformNode.setPivotPoint(tv.position);
  
    // head.rotation.x = Math.PI/2;
    // tv.rotation.x = Math.PI/2;
  
    const result: MeshesForUser = {
      root,
      head,
      headTransformNode,
      nameTag,
      torso,
      torsoTransformNode,
      tv
    };
    return result;
  }

  public createUserMesh = (user: User) => {
    const meshesForUser = this.createMeshesForUser(user);
    const userMesh = this.userMeshType === 'UserMeshBasic' ? new UserMeshBasic(user, meshesForUser, this.scene) : new UserMeshMinimal(user, meshesForUser, this.scene);
    return userMesh;
  }
  
  public addUser = (user: User) => {
    const userMesh = this.createUserMesh(user);
    if (this.getNumUsers() === 0) this.nonInstancedUserId = user.id;
    this.userMeshes[user.id] = userMesh;
    console.log('added user: ', user.id);
    return userMesh;
  }

  public getUserMesh = (userId: string) => {
    return this.userMeshes[userId];
  }

  public removeUser = (userId: string) => {
    this.removeAllUserMeshFromScene(userId);
    delete this.userMeshes[userId];
  }

  public getUserMeshes = () => {
    return Object.values(this.userMeshes);
  }

  public getUserIds = () => {
    return Object.keys(this.userMeshes);
  }
  
  public getNumUsers = () => {
    return this.getUserIds().length;
  }

  public getRootMeshForUser = (userId: string = this.localUserId) => {
    return userId === this.localUserId ? this.localUserMesh : this.userMeshes[userId].root;
  }


  public groupUserIdsByDistance(sourceId: string) {
    const sourceUser = this.userMeshes[sourceId];
    const userIdsWithoutSource = Object.keys(this.userMeshes).filter(userId => userId !== sourceUser.user.id);
    const groupedUserIds = [];
  
    const getIsolatedUserIds = () => {
      const minDistanceForGroup = 8;
      const isolatedUserIds: string[] = [];
      for (const userId of userIdsWithoutSource) {
        let isIsolated = true;
        const otherUserIds = userIdsWithoutSource.filter(id => id !== userId);
        for (const otherUserId of otherUserIds) {
          const distance = getDistance(this.userMeshes[userId].root.position, this.userMeshes[otherUserId].root.position);
          if (distance < minDistanceForGroup) {
            isIsolated = false;
            break;
          }
        }
        if (isIsolated) groupedUserIds.push(userId);
      }
      return isolatedUserIds;
    }
  
    const isolatedUserIds = getIsolatedUserIds();
    /* If userId doesn't have anyone within X distance of them, they get a solo group
       Otherwise, take make a super group with all userIds that connect within X distance
       if A is within X of B, and B is within X of C, A,B,C are in a group even if A is more than X from C
       For supergroup, check distances within group by nominating each candidate
       The one with smallest distance(sq?) sum gets nominated, and all members within X get grouped with it
  
        A--B--C--D--E--F--G (one super group) -- B-F are a candidate, of which anything but D is equivalently good
        A--B--C    D--E--F (two super groups) -- B and E are winning candidates x=~8
    */
  }
  
  public sortUserIdsByDistance(sourceRootMesh: TransformNode) {
    const sortedUserIds = Object.keys(this.userMeshes).sort((a, b) => {
      const distanceToA = getDistance(sourceRootMesh.position, this.userMeshes[a].root.position);
      const distanceToB = getDistance(sourceRootMesh.position, this.userMeshes[b].root.position);
      return distanceToA < distanceToB ? -1 : distanceToA === distanceToB ? 0 : 1;
    });
    return sortedUserIds;
  }
  
  public getDistanceRangeForUserIds(sourceRootMesh: TransformNode) {
    const sortedUserIds = this.sortUserIdsByDistance(sourceRootMesh);
    if (sortedUserIds.length === 0) return new Vector2(0,0);
    const minDistance = getDistance(sourceRootMesh.position, this.userMeshes[sortedUserIds[0]].root.position);
    const maxDistance = getDistance(sourceRootMesh.position, this.userMeshes[sortedUserIds[sortedUserIds.length - 1]].root.position);
    return new Vector2(minDistance, maxDistance);
  }
  
  public sortUserIdsBySourceAngle(sourceRootMesh: TransformNode) {
    const sortedUserIds = Object.keys(this.userMeshes).sort((a, b) => {
      const angleToA = calculateAngleInfoFromSourceToTarget(sourceRootMesh.position, sourceRootMesh.rotation, this.userMeshes[a].root.position);
      const angleToB = calculateAngleInfoFromSourceToTarget(sourceRootMesh.position, sourceRootMesh.rotation, this.userMeshes[b].root.position);
      return angleToA.shortestAngleRadians < angleToB.shortestAngleRadians ? 1 : angleToA.shortestAngleRadians === angleToB.shortestAngleRadians ? 0 : -1;
    });
    return sortedUserIds;
  }
  
  public sortUserIdsByTargetAngle(sourceRootMesh: TransformNode) {
    const sortedUserIds = Object.keys(this.userMeshes).sort((a, b) => {
      const angleFromA = calculateAngleInfoFromSourceToTarget(this.userMeshes[a].root.position, this.userMeshes[a].root.rotation, sourceRootMesh.position);
      const angleFromB = calculateAngleInfoFromSourceToTarget(this.userMeshes[b].root.position, this.userMeshes[b].root.rotation, sourceRootMesh.position);
      return angleFromA.shortestAngleRadians < angleFromB.shortestAngleRadians ? -1 : angleFromA.shortestAngleRadians === angleFromB.shortestAngleRadians ? 0 : 1;
    });
    return sortedUserIds;
  }
}